/**
 * The little notepad that floats above Study Buddy during a chat session —
 * Buddy sits at its bottom-right corner, so it feels like Buddy is writing.
 */
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Loader2, NotebookPen, Sparkles, X, MousePointerClick } from 'lucide-react';
import { toast } from 'sonner';
import { captureNote, deleteNote, fetchNotes } from '@/lib/notes';
import NoteTree from '@/components/notes/NoteTree';

export function NotesPad({ context, side = 'right', autoCapture, refreshKey, onClose }) {
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [saving, setSaving] = useState(false);
  const sessionId = context?.sessionId;

  const load = useCallback(async () => {
    if (!sessionId) return;
    try { setGroups((await fetchNotes({ sessionId })).groups || []); } catch { /* keep last view */ }
  }, [sessionId]);

  const saveLastAnswer = useCallback(async () => {
    if (!sessionId || saving) return;
    setSaving(true);
    try {
      const r = await captureNote({ session_id: sessionId, mode: 'answer' });
      if (r.already_saved) toast.message('That answer is already in your notes');
      else toast.success(`Noted ${r.notes.length} point${r.notes.length === 1 ? '' : 's'}`);
      await load();
    } catch (e) {
      toast.error(e?.message || 'Could not take that note');
    } finally { setSaving(false); }
  }, [sessionId, saving, load]);

  useEffect(() => { load(); }, [load, refreshKey]);
  useEffect(() => { if (autoCapture) saveLastAnswer(); }, [autoCapture]); // eslint-disable-line react-hooks/exhaustive-deps

  const remove = async (id) => {
    try { await deleteNote(id); await load(); } catch { toast.error('Could not remove that point'); }
  };

  const headings = groups.flatMap(g => g.chapters).flatMap(c => c.headings);

  return (
    <motion.div
      initial={{ opacity: 0, y: 12, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 12, scale: 0.96 }}
      transition={{ type: 'spring', stiffness: 260, damping: 24 }}
      onPointerDownCapture={e => e.stopPropagation()}
      className={`absolute bottom-[calc(100%+0.5rem)] ${side === 'left' ? 'left-0 origin-bottom-left' : 'right-0 origin-bottom-right'} w-[min(340px,calc(100vw-2.5rem))] cursor-auto`}
      data-testid="notes-pad">
      <div className="rounded-3xl bg-[#FFFDF5] border border-amber-200 shadow-2xl overflow-hidden"
        style={{ backgroundImage: 'repeating-linear-gradient(180deg, transparent 0 27px, rgba(217,119,6,.08) 27px 28px)' }}>
        <div className="flex items-center justify-between px-4 pt-3 pb-2 border-b border-amber-100 bg-amber-50/80">
          <p className="font-heading text-sm font-bold text-amber-900 flex items-center gap-1.5">
            <NotebookPen className="w-4 h-4" /> Notes from this session
          </p>
          <button onClick={onClose} data-testid="notes-pad-close" className="text-amber-700/70 hover:text-amber-900"><X className="w-4 h-4" /></button>
        </div>
        <div className="max-h-[min(24vh,300px)] sm:max-h-[min(36vh,300px)] overflow-y-auto px-4 py-3">
          {headings.length ? <NoteTree headings={headings} onDelete={remove} compact /> : (
            <p className="text-[12.5px] font-body text-slate-500" data-testid="notes-pad-empty">
              {saving ? 'Writing it down…' : 'Nothing noted yet. Tap below and I\'ll jot down the key points.'}
            </p>
          )}
        </div>
        <div className="px-4 pb-3 pt-2 border-t border-amber-100 bg-white/70 space-y-2">
          <button onClick={saveLastAnswer} disabled={saving} data-testid="notes-pad-save-answer"
            className="w-full inline-flex items-center justify-center gap-1.5 rounded-full bg-amber-500 hover:bg-amber-600 disabled:opacity-60 text-white text-xs font-heading py-2 transition-colors">
            {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
            Note the key points of the last answer
          </button>
          <p className="text-[11px] font-body text-slate-500 flex items-center gap-1.5">
            <MousePointerClick className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" />
            Or select any words in a reply to save just those.
          </p>
          <button onClick={() => navigate('/notes')} data-testid="notes-pad-view-all"
            className="text-[11px] font-heading text-amber-700 hover:underline">All my notes →</button>
        </div>
      </div>
    </motion.div>
  );
}

export default NotesPad;
